"use client";

import React, { useState } from "react";
import { BookOpen, CircleCheck as CheckCircle2, ExternalLink, ShieldCheck, Sparkles, Star, Youtube } from 'lucide-react';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const takeaways = [{
  title: "Discover Biblical Direction",
  description: "Find clear, faith-centered principles to guide your daily walk and decisions."
}, {
  title: "Overcome Uncertainty",
  description: "Build firm, resilient faith to navigate seasons of doubt and change."
}, {
  title: "Cultivate Lasting Legacy",
  description: "Establish spiritual habits that create an enduring legacy for your family."
}];
export default function BookHero() {
  const [isExpanded, setIsExpanded] = useState(false);
  return <section id="bookheropurposedrivenlegacy" data-nav="light" className="relative overflow-hidden bg-background py-10 md:py-14 lg:py-16">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/2 -z-10 hidden h-96 w-full -translate-x-1/2 max-w-7xl rounded-full bg-accent/10 blur-3xl md:block" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 right-0 -z-10 hidden h-80 w-80 rounded-full bg-primary/5 blur-3xl md:block" />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">
          
          {/* Left Column: Text Content & Actions */}
          <div className="order-2 flex flex-col justify-center lg:order-1 lg:col-span-7">
            {/* Category / Badge */}
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="outline" className="border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent-foreground">
                <Sparkles className="size-3.5 text-accent" />
                Featured Christian Release
              </Badge>
            </div>

            {/* Main Editorial Heading */}
            <h1 className="mt-4 font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-5xl lg:leading-tight">
              Purpose Driven Legacy: <span className="text-accent">Living a life worthy of legacy</span>
            </h1>

            {/* Author Attribution */}
            <p className="mt-2 text-base font-medium tracking-wide text-muted-foreground sm:text-lg">
              By <span className="text-foreground">Dustin Royer</span>
            </p>

            {/* Author Attribution & Subheading */}
            <p className="mt-3 text-lg font-medium text-secondary-foreground sm:text-xl">
              Living a Life Worth Of Legacy
            </p>

            {/* Faith-Centered Narrative Description */}
            <div className="mt-4">
              <div className={`overflow-hidden transition-[max-height] duration-300 ${isExpanded ? "max-h-none" : "max-h-24 sm:max-h-28"}`}>
                <p className="text-base leading-relaxed text-muted-foreground">
                  In a world crowded with fleeting distractions, Dustin Royer presents a clear, scripture-grounded pathway to cultivating faithful stewardship, spiritual depth, and an enduring legacy that honors Christ across every generation.
                </p>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  Through deeply rooted biblical insights, real-life application, and practical spiritual disciplines, this book empowers you to move beyond passive faith into intentional Kingdom living. Discover how everyday decisions, work, family rhythms, and leadership can become lasting instruments of God's grace.
                </p>
                <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                  Whether you are mentoring the next generation, seeking clarity in your calling, or desiring to leave a faithful imprint on those you lead, Purpose Driven Legacy provides the timeless guidance and conviction you need to build a life that stands the test of time.
                </p>
              </div>
              <Button variant="link" onClick={() => setIsExpanded(prev => !prev)} className="h-auto p-0 pt-1 text-sm font-medium text-accent hover:underline">
                {isExpanded ? "Read less" : "Read more"}
              </Button>
            </div>

            <Separator className="my-6 max-w-md bg-border/80" />

            {/* Key Takeaways with Subtle Iconography */}
            <div className="space-y-3.5">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                What You Will Discover Inside
              </h2>
              <div className="space-y-3">
                {takeaways.map((takeaway, i) => <div key={takeaway.title} data-index={i} className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-accent/20 text-accent-foreground">
                      <CheckCircle2 className="size-3.5 text-accent" />
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">{takeaway.title}: </span>
                      <span className="text-sm text-muted-foreground">{takeaway.description}</span>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* CTA Action Buttons */}
            <div className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="group relative shadow-md transition-[color,background-color,border-color,box-shadow]">
                <Link to="https://a.co/d/0fajSfsP" newTab className="inline-flex items-center gap-2">
                  <span>Buy on Amazon</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="group border border-border/80 bg-card/50 text-foreground transition-[color,background-color,border-color,box-shadow] hover:bg-card">
                <Link to="https://www.youtube.com/@RootedDisciples" newTab className="inline-flex items-center gap-2">
                  <Youtube className="size-4" />
                  <span>View Study Resources</span>
                  <ExternalLink className="size-4 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
              </Button>
            </div>

            {/* Micro Trust Banner */}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-accent" />
                <span>Available in Paperback & Kindle</span>
              </div>
              <span className="hidden sm:inline text-border">•</span>
              <div className="flex items-center gap-1">
                <div className="flex text-accent">
                  {[...Array(5)].map((_, i) => <Star key={i} className="size-3 fill-accent text-accent" />)}
                </div>
                <span className="font-medium text-foreground">5-Star Reader Reviews</span>
              </div>
            </div>
          </div>

          {/* Right Column: Book Showcase Card (Displayed above text on mobile) */}
          <div className="order-1 flex flex-col items-center justify-center lg:order-2 lg:col-span-5">
            <div className="relative w-full max-w-xs sm:max-w-sm lg:max-w-md">
              {/* Subtle back ambient glow */}
              <div aria-hidden="true" className="absolute inset-0 -z-10 scale-95 rounded-2xl bg-gradient-to-tr from-accent/30 via-primary/10 to-transparent blur-2xl" />

              <div className="relative overflow-hidden rounded-lg border border-border bg-card p-3 shadow-2xl sm:p-4">
                {/* Book Cover Container with Aspect Ratio */}
                <div className="relative overflow-hidden rounded-md shadow-lg ring-1 ring-border/50">
                  <AspectRatio ratio={2 / 3} className="bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/KDP_PRINT_BOOK_PUBLISHER_COVER.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/KDP_PRINT_BOOK_PUBLISHER_COVER.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679939/img/KDP_PRINT_BOOK_PUBLISHER_COVER.jpg-wvc-sizes" alt="Purpose Driven Legacy by Dustin Royer book cover" className="size-full object-cover object-center" loading="lazy" decoding="async" />
                  </AspectRatio>

                  {/* Subtle Sheen Highlight Overlay */}
                  <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-60" />
                </div>

                {/* Footnote Bar */}
                <div className="mt-3 flex items-center justify-between px-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <BookOpen className="size-3.5 text-accent" />
                    Paperback & Kindle
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="flex size-7 items-center justify-center rounded-md bg-accent/20 text-accent-foreground">
                      <BookOpen className="size-4 text-accent" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs font-semibold text-foreground">Dustin Royer</p>
                      <p className="text-[11px] text-muted-foreground">Author & Bible Teacher</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}